import React from 'react'
import * as ROUTES from 'constants/routes'
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Icon,
  Link,
  Loading,
  Message,
  Text,
  Tooltip,
} from '@workwhile/ui'
import { ExternalLinkIcon, InfoIcon, TerminalIcon } from 'lucide-react'
import { format } from 'date-fns'
import { useFieldglassReferenceDataContextValue } from 'pages/Fieldglass/shared/context/FieldglassReferenceDataProvider'
import { ResolveFieldglassIssueButton } from 'pages/Fieldglass/shared/components/ResolveFieldglassIssueButton'
import type {
  FieldglassJobPostingIssue,
  FieldglassJobPostingShift,
  FieldglassJobPostingWorker,
} from 'api/admin/fieldglass/getFieldglassJobPosting'
import { useFieldglassJobPostingContextValue } from '../context/FieldglassJobPostingProvider'

const buildFieldglassCommandsUrl = ({
  jobPostingId,
  shiftId,
}: {
  jobPostingId: string
  shiftId?: number
}) => {
  const params = new URLSearchParams({
    search: 'fieldglass',
    job_posting_id: jobPostingId,
  })
  if (shiftId !== undefined) {
    params.set('shift_id', shiftId.toString())
    params.set('shifts', shiftId.toString())
  }

  return `${ROUTES.COMMANDS}?${params.toString()}`
}

const formatShiftTime = (isoTime: string) =>
  format(new Date(isoTime), 'EEEE, MMM d, h:mm a')

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

const DetailRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => {
  return (
    <Flex gap={2} alignItems="baseline">
      <Text fontSize={1} color="lightText" style={{ minWidth: 72 }}>
        {label}
      </Text>
      <Box flex={1}>{children}</Box>
    </Flex>
  )
}

const NamedId = ({ name, id }: { name: string | null; id: number | null }) => {
  if (name === null || id === null) {
    return <Text>-</Text>
  }

  return (
    <Text>
      {name} ({id})
    </Text>
  )
}
const _ISSUE_ROW_HEIGHT = 28

const WorkerIssues = ({ issues }: { issues: FieldglassJobPostingIssue[] }) => {
  if (issues.length === 0) {
    return <Text color="lightText">No issues</Text>
  }

  return (
    <Flex flexDirection="column" gap={1}>
      {issues.map((issue) => (
        <Flex
          key={issue.id}
          alignItems="center"
          gap={1}
          height={_ISSUE_ROW_HEIGHT}
        >
          <Text fontSize={1}>{issue.issueTypeLabel}</Text>
          <Tooltip content={issue.message}>
            <Icon icon={InfoIcon} size={14} />
          </Tooltip>
        </Flex>
      ))}
    </Flex>
  )
}

const JobPostingIssues = ({
  issues,
}: {
  issues: FieldglassJobPostingIssue[]
}) => {
  if (issues.length === 0) {
    return <Text color="lightText">No issues</Text>
  }

  return (
    <Flex flexDirection="column" gap={2}>
      {issues.map((issue) => (
        <Box key={issue.id}>
          <Flex alignItems="center" gap={2} flexWrap="wrap">
            <Text fontSize={1}>{issue.issueTypeLabel}</Text>
            {issue.workerName === null ? null : (
              <Text fontSize={1} color="lightText">
                {issue.workerName}
              </Text>
            )}
            <ResolveFieldglassIssueButton
              issueId={issue.id}
              issueType={issue.issueType}
            />
          </Flex>
          <Text fontSize={1} color="lightText">
            {issue.message}
          </Text>
        </Box>
      ))}
    </Flex>
  )
}

const WorkerIssueActions = ({
  issues,
}: {
  issues: FieldglassJobPostingIssue[]
}) => {
  return (
    <Flex flexDirection="column" gap={1}>
      {issues.map((issue) => (
        <Flex key={issue.id} alignItems="center" height={_ISSUE_ROW_HEIGHT}>
          <ResolveFieldglassIssueButton
            issueId={issue.id}
            issueType={issue.issueType}
          />
        </Flex>
      ))}
    </Flex>
  )
}

const WorkerName = ({ worker }: { worker: FieldglassJobPostingWorker }) => {
  return (
    <Text>
      {worker.workerName ?? 'Unknown'} (
      <Link
        to={ROUTES.USER_DETAIL.replace(':userId', worker.workerId.toString())}
        target="_blank"
      >
        {worker.workerId}
      </Link>
      )
    </Text>
  )
}

const WorkerStatus = ({ worker }: { worker: FieldglassJobPostingWorker }) => {
  return (
    <Text fontSize={1} color="lightText">
      {worker.status}
    </Text>
  )
}

const WorkersTable = ({
  workers,
}: {
  workers: FieldglassJobPostingWorker[]
}) => {
  if (workers.length === 0) {
    return <Text color="lightText">No workers on this shift.</Text>
  }

  return (
    <Box style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                padding: '4px 8px 4px 0',
                width: '30%',
              }}
            >
              <Text fontWeight="bold" fontSize={1}>
                Worker
              </Text>
            </th>
            <th style={{ textAlign: 'left', padding: '4px 8px', width: '15%' }}>
              <Text fontWeight="bold" fontSize={1}>
                Status
              </Text>
            </th>
            <th style={{ textAlign: 'left', padding: '4px 8px', width: '35%' }}>
              <Text fontWeight="bold" fontSize={1}>
                Issues
              </Text>
            </th>
            <th style={{ textAlign: 'left', padding: '4px 8px', width: '20%' }}>
              <Text fontWeight="bold" fontSize={1}>
                Actions
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {workers.map((worker) => (
            <tr
              key={worker.workerId}
              style={{ borderTop: '1px solid #e8e8e8' }}
            >
              <td style={{ padding: '8px 8px 8px 0', verticalAlign: 'top' }}>
                <WorkerName worker={worker} />
              </td>
              <td style={{ padding: '8px', verticalAlign: 'top' }}>
                <WorkerStatus worker={worker} />
              </td>
              <td style={{ padding: '8px', verticalAlign: 'top' }}>
                <WorkerIssues issues={worker.issues} />
              </td>
              <td style={{ padding: '8px', verticalAlign: 'top' }}>
                <WorkerIssueActions issues={worker.issues} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  )
}

const OpenFieldglassButton = ({
  companyGroupId,
}: {
  companyGroupId: number
}) => {
  const {
    state: { companyGroups },
  } = useFieldglassReferenceDataContextValue()

  const buyerUrl = companyGroups.find(
    (group) => group.companyGroupId === companyGroupId
  )?.buyerUrl

  if (buyerUrl === undefined) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buyerUrl.replace(/\/$/, '')}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Open Fieldglass</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const RunCommandButton = ({
  jobPostingId,
  shiftId,
}: {
  jobPostingId: string
  shiftId?: number
}) => {
  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buildFieldglassCommandsUrl({ jobPostingId, shiftId })}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Run command</Text>
        <Icon icon={TerminalIcon} size={12} />
      </Flex>
    </Button>
  )
}

const OpenWorkWhileShiftButton = ({ shiftId }: { shiftId: number }) => {
  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={ROUTES.SHIFT_DETAIL.replace(':shiftId', shiftId.toString())}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">View shift</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const FieldglassJobPostingCard = ({
  jobPosting,
}: {
  jobPosting: NonNullable<
    ReturnType<typeof useFieldglassJobPostingContextValue>['state']['data']
  >
}) => {
  const workerCount = jobPosting.shifts.reduce(
    (total, shift) => total + shift.workers.length,
    0
  )

  return (
    <Card p={3}>
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        gap={3}
        mb={3}
        flexWrap="wrap"
      >
        <Box>
          <Heading level={4} my={0}>
            {jobPosting.jobPostingId}
          </Heading>
          <Text fontSize={1} color="lightText" mt={1}>
            {pluralize(jobPosting.shifts.length, 'shift')} ·{' '}
            {pluralize(workerCount, 'worker')}
          </Text>
        </Box>
        <Flex gap={2} flexWrap="wrap" alignItems="flex-start">
          <OpenFieldglassButton companyGroupId={jobPosting.companyGroupId} />
          <RunCommandButton jobPostingId={jobPosting.jobPostingId} />
        </Flex>
      </Flex>

      <Flex gap={4} flexWrap="wrap">
        <Box flex={1} style={{ minWidth: 240 }}>
          <Text fontWeight="bold" mb={2}>
            WorkWhile
          </Text>
          <Flex flexDirection="column" gap={1}>
            <DetailRow label="Group">
              <NamedId
                name={jobPosting.companyGroupName}
                id={jobPosting.companyGroupId}
              />
            </DetailRow>
          </Flex>
        </Box>
      </Flex>

      <Box mt={3}>
        <Text fontWeight="bold" mb={2}>
          Issues
        </Text>
        {}
        <JobPostingIssues issues={jobPosting.issues} />
      </Box>
    </Card>
  )
}

const FieldglassJobPostingShiftCard = ({
  shift,
  jobPostingId,
}: {
  shift: FieldglassJobPostingShift
  jobPostingId: string
}) => {
  return (
    <Card p={3}>
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        gap={3}
        mb={3}
        flexWrap="wrap"
      >
        <Box>
          <Heading level={4} my={0}>
            Shift {shift.shiftId}
            {shift.cancelled ? ' (cancelled)' : ''}
          </Heading>
          <Text mt={1}>
            {formatShiftTime(shift.startsAt)}
            {' – '}
            {formatShiftTime(shift.endsAt)}
          </Text>
          <Text fontSize={1} color="lightText" mt={1}>
            {pluralize(shift.workers.length, 'worker')}
          </Text>
        </Box>
        <Flex gap={2} flexWrap="wrap" alignItems="flex-start">
          <OpenWorkWhileShiftButton shiftId={shift.shiftId} />
          <RunCommandButton
            jobPostingId={jobPostingId}
            shiftId={shift.shiftId}
          />
        </Flex>
      </Flex>

      <Flex gap={4} flexWrap="wrap">
        <Box flex={1} style={{ minWidth: 240 }}>
          <Text fontWeight="bold" mb={2}>
            WorkWhile
          </Text>
          <Flex flexDirection="column" gap={1}>
            <DetailRow label="Position">
              <Text>{shift.positionName ?? '-'}</Text>
            </DetailRow>
            <DetailRow label="Location">
              <Text>{shift.locationName ?? '-'}</Text>
            </DetailRow>
            <DetailRow label="Shift">
              <Text>
                <Link
                  to={ROUTES.SHIFT_DETAIL.replace(
                    ':shiftId',
                    shift.shiftId.toString()
                  )}
                  target="_blank"
                >
                  {shift.shiftId}
                </Link>
                {shift.cancelled ? ' (cancelled)' : ''}
              </Text>
            </DetailRow>
          </Flex>
        </Box>
      </Flex>

      <Box mt={3}>
        <Text fontWeight="bold" mb={2}>
          Workers
        </Text>
        {}
        <WorkersTable workers={shift.workers} />
      </Box>
    </Card>
  )
}

export const FieldglassJobPostingCards = () => {
  const {
    state: { data, isPending, isError, error, hasSearchParams },
  } = useFieldglassJobPostingContextValue()

  if (!hasSearchParams) {
    return (
      <Message variant="info" borderRadius="small">
        Enter a Fieldglass job posting ID to view its shifts and issues.
      </Message>
    )
  }

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" borderRadius="small">
        {error ||
          'Sorry, something went wrong. Please reach out to eng support if this persists.'}
      </Message>
    )
  }

  if (!data) {
    return null
  }

  return (
    <Flex flexDirection="column" gap={3}>
      <FieldglassJobPostingCard jobPosting={data} />
      {data.shifts.length === 0 ? (
        <Text color="lightText">No shifts mapped to this job posting.</Text>
      ) : (
        data.shifts.map((shift) => (
          <FieldglassJobPostingShiftCard
            key={shift.shiftId}
            shift={shift}
            jobPostingId={data.jobPostingId}
          />
        ))
      )}
    </Flex>
  )
}
